<template>
    <div class="home__touch__form getintouch__form" :data-gsap="gsapProps.animation" :data-gsap-delay="gsapProps.delay" :data-gsap-duration="gsapProps.duration" :data-gsap-ease="gsapProps.ease">
        <div v-if="formStatus.success" class="form-message success">
            {{ formStatus.message }}
        </div>
        <form v-else @submit.prevent="submitForm">
            <div class="getintouch__form__row">
                <input-component
                    v-model="formData.name"
                    :label="labels.name"
                    type="text"
                    :placeholder="placeholders.name"
                    required
                    aria-required="true">
                </input-component>

                <input-component
                    v-model="formData.email"
                    :label="labels.email"
                    type="email"
                    :placeholder="placeholders.email"
                    required
                    aria-required="true">
                </input-component>
            </div>

            <div class="form__field">
                <label :for="messageInputId">
                    {{ labels.message }}
                    <span class="required-mark">*</span>
                </label>

                <textarea 
                    :id="messageInputId"
                    rows="10" 
                    :placeholder="placeholders.message"
                    v-model="formData.message"
                    required
                    aria-required="true">
                </textarea>
            </div>

            <button class="general-button" type="submit">Send message</button>
            <div v-if="formStatus.error" class="form-message error">
                {{ formStatus.message }}
            </div>
        </form>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import axios from 'axios'
import InputComponent from './InputComponent.vue'

interface FormConfig {
    labels: {
        name: string
        email: string
        message: string
    }
    placeholders: {
        name: string
        email: string
        message: string
    }
}

const formConfigs: Record<string, FormConfig> = {
    general: {
        labels: {
            name: 'Your Name',
            email: 'Your Email',
            message: 'Message'
        },
        placeholders: {
            name: 'Your Name',
            email: 'Your Email',
            message: 'Your message'
        }
    },
    project: {
        labels: {
            name: 'Name',
            email: 'Email',
            message: 'Tell us about your project'
        },
        placeholders: {
            name: 'Your full name',
            email: 'Your email address',
            message: 'Message'
        }
    }
}

export default defineComponent({
    name: 'ContactForm',
    components: {
        InputComponent
    },
    props: {
        type: {
            type: String,
            default: 'general',
            validator: (value: string) => ['general', 'project'].includes(value)
        },
        gsapAnimation: {
            type: String,
            default: 'fadeIn'
        },
        gsapDelay: {
            type: String,
            default: '0.3'
        },
        gsapDuration: {
            type: String,
            default: '0.2'
        },
        gsapEase: {
            type: String,
            default: 'none'
        }
    },
    setup(props) {
        const homeurl: string = import.meta.env.VITE_API_URL

        const config = computed(() => formConfigs[props.type])
        const labels = computed(() => config.value.labels)
        const placeholders = computed(() => config.value.placeholders)
        
        const gsapProps = computed(() => ({
            animation: props.gsapAnimation,
            delay: props.gsapDelay,
            duration: props.gsapDuration,
            ease: props.gsapEase
        }))

        const messageInputId = computed(() => `messageInput-${props.type}`)

        const formData = ref({
            name: '',
            email: '',
            message: ''
        })

        const formStatus = ref({
            success: false,
            error: false,
            message: ''
        })

        const submitForm = () => {
            formStatus.value = {
                success: false,
                error: false,
                message: ''
            }

            // Determine source based on form type
            const source = props.type === 'general' ? 'homepage' : 'contact_page'

            axios.post(`${homeurl}/api/contact-form/submit`, {
                name: formData.value.name,
                email: formData.value.email,
                message: formData.value.message,
                source: source
            })
            .then((res) => {
                formStatus.value = {
                    success: true,
                    error: false,
                    message: res.data.message || 'Thanks for getting in touch with us! A Rain team member will get back to you. ☔'
                }
                // Clear the form
                formData.value = {
                    name: '',
                    email: '',
                    message: ''
                }
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || 'Sorry, there was an error submitting your message. Please try again.'
                formStatus.value = {
                    success: false,
                    error: true,
                    message: errorMessage
                }
            })
        }

        return {
            labels,
            placeholders,
            gsapProps,
            messageInputId,
            formData,
            formStatus,
            submitForm
        }
    }
})
</script>